import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API } from "@/lib/api";
import { ArrowUpRight, Wine, Utensils, Users } from "lucide-react";
import FadeIn from "@/components/FadeIn";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_modern-bistro-portal/artifacts/eybg8wfd_B67AB930-114C-44B3-A785-FC3F8B9BA094.PNG";
const ABOUT_IMG = "https://images.unsplash.com/photo-1751890939642-52aa0d543bd0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxnb3VybWV0JTIwaXRhbGlhbiUyMGRpc2glMjBwbGF0aW5nJTIwcmVzdGF1cmFudHxlbnwwfHx8fDE3NzgwOTQ1NDF8MA&ixlib=rb-4.1.0&q=85";
const CITY_IMG = "https://images.unsplash.com/photo-1774433074742-ce92958437d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHx2aWNlbnphJTIwaXRhbHklMjBwYWxsYWRpYW4lMjBhcmNoaXRlY3R1cmUlMjBuaWdodHxlbnwwfHx8fDE3NzgwOTQ1NDF8MA&ixlib=rb-4.1.0&q=85";
const CTA_BG = "https://customer-assets.emergentagent.com/job_modern-bistro-portal/artifacts/cvy0c0vy_IMG_8320.jpg";

const DEFAULT_GALLERY = [
  "https://images.unsplash.com/photo-1551183053-bf91a1d81141?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1473093295043-cdd812d0e601?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1572715376701-98568319fd0b?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1546833999-b9f581a1996d?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1565958011703-44f9829ba187?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?auto=format&fit=crop&w=900&q=80",
];

const ASPECTS = ["aspect-[3/4]", "aspect-square", "aspect-square", "aspect-[3/4]", "aspect-[3/4]", "aspect-square", "aspect-square", "aspect-[3/4]"];

const resolveImg = (u) => {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  return `${API.replace(/\/api$/, "")}${u}`;
};

export default function Home() {
  const { t } = useTranslation();
  const [gallery, setGallery] = useState([]);
  const [siteImages, setSiteImages] = useState({});
  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => setGallery([]));
    api.get("/site-images").then((r) => setSiteImages(r.data || {})).catch(() => setSiteImages({}));
  }, []);
  const photos = gallery.length > 0
    ? gallery.map((g) => resolveImg(g.image_url))
    : DEFAULT_GALLERY;
  const heroImg = resolveImg(siteImages["home.hero"]) || HERO_IMG;
  const aboutImg = resolveImg(siteImages["home.about"]) || ABOUT_IMG;
  const ctaBg = resolveImg(siteImages["home.cta"]) || CTA_BG;
  const cityImg = resolveImg(siteImages["home.city"]) || CITY_IMG;
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        {/* Sharp full-image background — logo & FUTUREBOND text remain visible */}
        <img
          src={heroImg}
          alt="Harry's interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pb-24 sm:pb-32 w-full">
          <div className="max-w-3xl fade-up">
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#F3F1EC]/85 font-medium">
              {t("hero.eyebrow")}
            </p>
            <div data-testid="hero-title" className="mt-4">
              <h1 className="font-script text-white text-7xl sm:text-9xl lg:text-[10rem] leading-[0.95] tracking-tight drop-shadow-[0_4px_30px_rgba(0,0,0,0.5)]">
                Harry's
              </h1>
              <p className="text-[11px] sm:text-sm tracking-[0.4em] uppercase text-white/95 mt-3 font-medium">
                Salotto &amp; Cucina
              </p>
            </div>
            <p className="font-serif text-white text-3xl sm:text-4xl tracking-tight mt-6 whitespace-pre-line">
              {t("hero.title")}
            </p>
            <p className="text-base sm:text-lg text-[#F3F1EC]/90 mt-6 max-w-xl leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link
                to="/menu"
                data-testid="hero-cta-menu"
                className="px-7 py-4 bg-[#FAF9F6] text-[#2C2C2A] hover:bg-white text-sm tracking-wide inline-flex items-center gap-2 transition-all hover:-translate-y-[2px]"
              >
                {t("hero.ctaMenu")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link
                to="/prenota"
                data-testid="hero-cta-book"
                className="px-7 py-4 bg-[#8B3A3A] text-white hover:bg-[#6C2A2A] text-sm tracking-wide inline-flex items-center gap-2 transition-all hover:-translate-y-[2px]"
              >
                {t("hero.ctaBook")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <FadeIn className="block overflow-hidden">
              <img src={aboutImg} alt="Gourmet plating" className="w-full aspect-[4/5] object-cover transition-transform duration-[1200ms] hover:scale-[1.04]" />
            </FadeIn>
          </div>
          <FadeIn delay={120} className="lg:col-span-7 lg:pl-12 order-1 lg:order-2">
            <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C59] font-semibold">
              {t("about.eyebrow")}
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1] mt-5">
              {t("about.title")}
            </h2>
            <p className="text-base sm:text-lg text-[#5C5C59] mt-8 max-w-2xl leading-relaxed">
              {t("about.body")}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
              {[
                { icon: Utensils, k: "1" },
                { icon: Wine, k: "2" },
                { icon: Users, k: "3" },
              ].map(({ icon: Icon, k }) => (
                <div key={k} className="border-t border-[#E5E3D8] pt-5">
                  <Icon className="w-5 h-5 text-[#8B3A3A] mb-3" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl tracking-tight">{t(`about.feature${k}Title`)}</h3>
                  <p className="text-sm text-[#5C5C59] mt-2 leading-relaxed">
                    {t(`about.feature${k}Body`)}
                  </p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>
      <section className="py-24 sm:py-32 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <FadeIn className="flex items-end justify-between gap-6 flex-wrap mb-12">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C59] font-semibold">
                {t("gallery.eyebrow")}
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-4 max-w-2xl">
                {t("gallery.title")}
              </h2>
            </div>
            <Link
              to="/galleria"
              data-testid="home-gallery-cta"
              className="inline-flex items-center gap-2 text-sm tracking-wide text-[#8B3A3A] link-underline"
            >
              {t("nav.gallery")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </FadeIn>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {photos.map((src, i) => (
              <FadeIn
                key={i}
                delay={Math.min(i, 7) * 50}
                data-testid={`gallery-photo-${i}`}
                className={`relative ${ASPECTS[i % ASPECTS.length]} overflow-hidden bg-[#E5E3D8] group`}
              >
                <img
                  src={src}
                  alt="Piatto"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section
        className="relative py-24 sm:py-36 border-y border-[#E5E3D8] overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${ctaBg}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-black/60" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid md:grid-cols-2 gap-10 items-center">
          <FadeIn>
            <p className="text-xs tracking-[0.3em] uppercase text-[#F3F1EC]/85 font-semibold">
              {t("quote.eyebrow")}
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 text-white">
              {t("quote.title")}
            </h2>
            <p className="text-[#F3F1EC]/90 mt-5 max-w-lg leading-relaxed text-base sm:text-lg">
              {t("quote.subtitle")}
            </p>
          </FadeIn>
          <FadeIn delay={120} className="flex md:justify-end">
            <Link
              to="/eventi"
              data-testid="home-quote-cta"
              className="px-7 py-4 bg-[#8B3A3A] text-white hover:bg-[#6C2A2A] inline-flex items-center gap-2 transition-all hover:-translate-y-[2px]"
            >
              {t("quote.submit")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* LOCATION TEASER */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <FadeIn className="lg:col-span-6">
            <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C59] font-semibold">
              {t("location.eyebrow")}
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-5">
              {t("location.title")}
            </h2>
            <p className="text-[#5C5C59] mt-6 leading-relaxed">{t("location.subtitle")}</p>
            <Link
              to="/dove-siamo"
              data-testid="home-location-cta"
              className="inline-flex items-center gap-2 mt-8 text-sm tracking-wide text-[#8B3A3A] link-underline"
            >
              {t("nav.location")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </FadeIn>
          <FadeIn delay={150} className="lg:col-span-6">
            <div className="overflow-hidden">
              <img src={cityImg} alt="Vicenza" className="w-full aspect-[5/4] object-cover transition-transform duration-[1200ms] hover:scale-[1.04]" />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
